import React from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { FormControl, Checkbox, Text, Link, VStack } from "@chakra-ui/react";

import { useLocale } from "contexts/LocaleContext/LocaleContextProvider";
import { ISignupSubmitData } from "models/User";
import { airhelpUtils } from "utils";
import { airLuggagePolicy, airPayoutPolicy } from "utils/insurancPolicy";

type FormType = UseFormReturn<ISignupSubmitData, object>;

const Consents: React.FC<{
  requireTerms?: boolean;
  requireMarketing?: boolean;
  form: FormType;
  isCompleteMembership?: boolean;
}> = ({ form, requireTerms, requireMarketing, isCompleteMembership }) => {
  const { locale } = useLocale();
  const { t } = useTranslation();
  const { control, formState, watch, setValue } = form;
  const { errors } = formState;
  const formFieldsWatch = watch();
  const { marketingConsent, privacyConsent } = formFieldsWatch;

  const handleSelectAll = () => {
    if (privacyConsent && marketingConsent) {
      setValue("privacyConsent", false, { shouldValidate: true });
      setValue("marketingConsent", false);
    } else {
      setValue("privacyConsent", true, { shouldValidate: true });
      setValue("marketingConsent", true);
    }
  };

  const consents = [privacyConsent, marketingConsent];
  const allChecked = consents.every(Boolean);
  const termsUrl = airhelpUtils.websiteUrls("termsPath", locale);
  const privacyUrl = airhelpUtils.websiteUrls("privacyPath", locale);
  const airLuggageTermsUrl = airLuggagePolicy(2);
  const airPayoutTermsUrl = airPayoutPolicy(true);

  return (
    <VStack alignItems="center" spacing={3} width="100%">
      {requireTerms && requireMarketing && (
        <FormControl w="100%">
          <Controller
            render={({ field: { name } }) => (
              <Checkbox
                size="lg"
                name={name}
                isChecked={allChecked}
                onChange={handleSelectAll}
                alignItems="flex-start"
                data-testid="select_all_consent"
              >
                <Text fontSize="sm" fontWeight="medium" color="greyscale.700">
                  {t("common.select_all")}
                </Text>
              </Checkbox>
            )}
            name="selectAllConsent"
            control={control}
            defaultValue={false}
          />
        </FormControl>
      )}
      {requireTerms && (
        <FormControl w="100%" isInvalid={!!errors.privacyConsent}>
          <Controller
            render={({ field: { onChange, name }, fieldState: { invalid } }) => (
              <Checkbox
                name={name}
                onChange={onChange}
                size="lg"
                isChecked={privacyConsent}
                isInvalid={invalid}
                alignItems="flex-start"
                data-testid="agree_tc_and_privacy_consent"
              >
                <Text fontSize="sm">
                  <Trans
                    i18nKey={
                      isCompleteMembership
                        ? "common.agree_tc_and_privacy_consent_air_payout_and_air_luggage"
                        : "common.agree_tc_and_privacy_consent_air_payout"
                    }
                    values={{
                      privacyUrl,
                      termsUrl,
                      airPayoutTermsUrl,
                      airLuggageTermsUrl,
                    }}
                    components={{
                      a: <Link isExternal fontSize="sm" fontWeight="normal" _visited={{}} />,
                    }}
                  />
                </Text>
              </Checkbox>
            )}
            name="privacyConsent"
            control={control}
            defaultValue={false}
            rules={{
              required: { value: true, message: t("errors.required") },
            }}
          />
        </FormControl>
      )}
      {requireMarketing && (
        <FormControl w="100%">
          <Controller
            render={({ field: { name, onChange } }) => (
              <Checkbox
                size="lg"
                name={name}
                onChange={onChange}
                isChecked={marketingConsent}
                alignItems="flex-start"
                data-testid="marketing_consent"
              >
                <Text fontSize="sm">
                  {t("common.marketing_consent")}
                  <Text fontSize="sm" color="greyscale.500" ml={1} as="span">
                    {t("optional")}
                  </Text>
                </Text>
              </Checkbox>
            )}
            name="marketingConsent"
            control={control}
            defaultValue={false}
          />
        </FormControl>
      )}
    </VStack>
  );
};

export default Consents;
